/* eslint-disable arrow-parens */
import React, { Suspense, useCallback, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormControl, Button, InputAdornment, SnackbarClassKey, Snackbar } from "@material-ui/core";
import ROLES from "../../../../../constants/roles";

import { ThemeCustomInputField } from "../../../../shared/themeComponents/themeCustomInputField";
import { trackPromise } from "react-promise-tracker";
import { api } from "../../../../../utils/api";
import validateEmail from "../../../../../utils/commonFunctions/validateEmail";
import MuiAlert from "@material-ui/lab/Alert";
import { Validation } from "../../../../../@types/validation.types";

// logos and icon
import AccountCircle from "@material-ui/icons/AccountCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { HiExternalLink } from "react-icons/hi";
import { nexcaliberLogo } from "../../../../../assets";
import { forgetPasswordUsernameImage } from "../../../../../assets";
import SelectMenuItems from "../../../../shared/selectMenuItems/SelectMenuItems";

import "./generatePasswordLink.scss";
import { ADMIN_ENV } from "../../../../../config/config";

const NewGeneratePasswordLink = () => {
	const [anchorElement, setAnchorElement] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [hasCreateClick, setHasCreateClick] = useState(false);
	const [openSnack, setOpenSnack] = useState(false);
	const [generatePasswordDetails, setGeneratePasswordDetails] = useState({
		credential: "",
		role: ""
	});
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event: React.SyntheticEvent<any, Event>, reason: SnackbarClassKey | any) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});
	const [validation, setValidation] = useState<Validation>({
		generatePasswordDetails: {},
		status: "invalid"
	});

	const navigate = useNavigate();
	const _validation = useRef<Validation>();
	const roles = Object.values(ROLES);

	const Alert = (props: any) => {
		return <MuiAlert elevation={6} variant="filled" {...props} />;
	};

	const handleValidation = useCallback(async () => {
		let email: any, username: any;
		const { role, credential } = generatePasswordDetails;
		_validation.current = {
			...validation,
			generatePasswordDetails: {},
			status: "invalid"
		};
		let flag = true;

		if (credential.includes("@")) email = credential;
		else username = credential;

		if (role.length === 0) {
			_validation.current.generatePasswordDetails["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (credential.length === 0) {
			_validation.current.generatePasswordDetails["credential"] = "Username/Email is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (credential.includes("@")) {
			if (!validateEmail(email)) {
				_validation.current.generatePasswordDetails["credential"] = "Valid Email is required";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [generatePasswordDetails, validation]);

	const handleInputFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		setGeneratePasswordDetails(Object.assign({}, generatePasswordDetails, { [name]: value }));
	};

	const handleCredentialRoleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, name: string) => {
			setAnchorElement(null);
			const value = event.currentTarget.getAttribute("value");
			setGeneratePasswordDetails(Object.assign({}, generatePasswordDetails, { [name]: value }));
		},
		[generatePasswordDetails]
	);

	const handleRoleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const { currentTarget } = event;
		setAnchorElement(currentTarget);
	};

	const handleRoleMenuClose = () => {
		setAnchorElement(null);
	};

	// RESET PASSWORD GENERATE LINK - BUTTON
	const handleGeneratePasswordLinkButtonClick = async (
		event: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>
	) => {
		try {
			event.preventDefault();
			const validation = await handleValidation();
			if (validation === "valid") {
				const payload = {
					...generatePasswordDetails,
					role: generatePasswordDetails.role.toUpperCase()
				};
				// console.log("Generate Password", payload);

				const response = await trackPromise(api.auth.generatePasswordLink(payload));
				// console.log("Response", response);
				if (response) {
					setHasCreateClick(true);
					setTimeout(() => {
						setSnackbarAPICallProps(
							Object.assign({}, snackbarAPICallProps, {
								open: true,
								message: "Recovery Link has been sent to the mail",
								severity: "success"
							})
						);
					}, 200);
					alert("A Recovery link has been sent to your email");
					navigate("/login");
				}
			} else {
				setHasCreateClick(false);
			}
		} catch (error) {
			setHasCreateClick(false);
			alert("Unable to sent email!");
			throw error;
		}
	};

	return (
		<div className="generate-password-link-main-container" id="generate-password-link-main-container">
			<div className="left-section">
				<img className="illustration" src={forgetPasswordUsernameImage} alt="" />
				<div className="tagline">
					<h3>Recoveries . . .</h3>
					<span>are very simple in our portal</span>
				</div>
			</div>
			<div className="right-section">
				{ADMIN_ENV === "DEV" ? (
					<div className="test-portal-login-container" style={{ marginTop: "20px" }}>
						<h2 className="test-portal-login-text">Test Portal</h2>
					</div>
				) : null}
				<img className="nexcaliber-logo" src={nexcaliberLogo} alt="" />
				<h3>
					Password <span>Recovery</span>
				</h3>
				<span className="subheading">This will generate a link to reset your password.</span>

				<form onSubmit={handleGeneratePasswordLinkButtonClick} autoComplete="off" method="">
					<FormControl className="form-container" id="form-container">
						<div className="select-role-container" id="select-role-container">
							<Button
								onClick={handleRoleClick}
								className="role-select-button"
								endIcon={<ExpandMoreIcon className="expand-icon" />}
							>
								{generatePasswordDetails.role !== ""
									? generatePasswordDetails.role
									: "Select Your Role"}
							</Button>
							<SelectMenuItems
								fieldName="role"
								anchorElement={anchorElement}
								listItems={roles}
								handleMenuClose={handleRoleMenuClose}
								handleSelecMenuItemClick={handleCredentialRoleChange}
							/>
							{validation.generatePasswordDetails.role ? (
								<div className="details" style={{ textAlign: "center" }}>
									<span className="select-validation-text">
										{validation.generatePasswordDetails.role}
									</span>
								</div>
							) : null}
						</div>
						<ThemeCustomInputField
							className="form-field-input"
							label="Enter your username/ email"
							name="credential"
							variant="outlined"
							value={generatePasswordDetails.credential}
							onChange={handleInputFieldChange}
							helperText={validation.generatePasswordDetails["credential"]}
							InputProps={{
								endAdornment: (
									<InputAdornment position="start">
										<AccountCircle className="adornment-color" />
									</InputAdornment>
								)
							}}
						/>

						<div
							className="generate-password-link-button-container"
							id="generate-password-link-button-container"
						>
							<Button
								className="generate-password-link-button"
								variant="contained"
								type="submit"
								disabled={hasCreateClick}
								onClick={handleGeneratePasswordLinkButtonClick}
							>
								<span className="button-label-with-icon">Generate Link</span>
								<span>
									<HiExternalLink className="button-icon" />
								</span>
							</Button>
						</div>

						<div className="sign-in-container">
							Are you a already a user? &nbsp;
							<span className="link-container">
								<Link className="sign-in-link" to="/login">
									Sign in
								</Link>
							</span>
						</div>
					</FormControl>
				</form>
				<div className="copyright-text-container">
					<span>© 2026 nexcaliber, All rights reserved</span>
				</div>
			</div>
			<Suspense fallback={<div />}>
				<Snackbar open={openSnack} onClose={snackbarAPICallProps?.handleSnackbarClose}>
					<Alert onClose={snackbarAPICallProps?.handleSnackbarClose} severity="success">
						A mail has been sent to your registered email with the reset password link
					</Alert>
				</Snackbar>
			</Suspense>
		</div>
	);
};

export default NewGeneratePasswordLink;
